<template>
  <section class="container w-[93%] md:w-full mx-auto my-12" id="contact">
    <div class="main max-w-7xl mx-auto p-4 grid grid-cols-12 relative">
      <div class="col-span-12 md:col-span-7">
        <h3 class="main-title">
          ¿Desea recibir información sobre nuevos cursos de formación?
        </h3>
        <p class="main-text">
          Déjenos su dirección de correo electrónico y le enviaremos las últimas
          noticias.
        </p>
        <form class="p-2" @submit.prevent="onSubmit">
          <div class="grid grid-cols-12 gap-4 my-4">
            <UiInput
              type="text"
              name="name"
              placeholder="Nombre"
              v-model="fields.name"
              class="col-span-12 md:col-span-6"
              :error="touched.name ? errors.name : ''"
              @blur="markTouched('name')"
            />
            <UiInput
              type="text"
              name="last_name"
              placeholder="Apellido"
              v-model="fields.surname"
              class="col-span-12 md:col-span-6"
              :error="touched.surname ? errors.surname : ''"
              @blur="markTouched('surname')"
            />
          </div>
          <div class="grid grid-cols-12 gap-4 mb-4">
            <UiInput
              type="text"
              name="company"
              placeholder="Empresa"
              v-model="fields.company"
              class="col-span-12 md:col-span-6"
              :error="touched.company ? errors.company : ''"
              @blur="markTouched('company')"
            />
            <UiInput
              type="text"
              name="post"
              placeholder="Cargo"
              v-model="fields.position"
              class="col-span-12 md:col-span-6"
              :error="touched.position ? errors.position : ''"
              @blur="markTouched('position')"
            />
          </div>
          <div class="grid grid-cols-12 gap-4 mb-4">
            <UiInput
              type="email"
              name="email"
              placeholder="Correo Electrónico"
              v-model="fields.email"
              class="col-span-12 md:col-span-6"
              :error="touched.email ? errors.email : ''"
              @blur="markTouched('email')"
            />
            <UiInput
              type="tel"
              name="phone"
              placeholder="Numero de teléfono"
              v-model="fields.phoneNumber"
              class="col-span-12 md:col-span-6"
              :error="touched.phoneNumber ? errors.phoneNumber : ''"
              @blur="markTouched('phoneNumber')"
            />
          </div>
          <div>
            <UiCheckBox
              v-model="fields.privacyConsent"
              name="accept"
              class="text-white"
              :error="touched.privacyConsent ? errors.privacyConsent : ''"
              @blur="markTouched('privacyConsent')"
              label="Acepto el procesamiento de mis datos personales según la política de privacidad."
            />
          </div>
          <button type="submit">Enviar</button>
        </form>
      </div>
      <div class="col-span-12 md:col-span-5 relative">
        <img
          :src="man"
          class="relative md:absolute h-[110%] hidden md:block bottom-[-1.1vw]"
          alt="man"
        />
      </div>
    </div>
  </section>
</template>
<script lang="ts" setup>
import man from "@/assets/img/global/man.png";
import { reactive, computed } from "vue";
const { sendRequest } = useContact();
const toast = useToast();

const fields = reactive({
  name: "",
  surname: "",
  company: "",
  position: "",
  email: "",
  phoneNumber: "",
  privacyConsent: false,
});

// touched map
const touched = reactive<Record<keyof typeof fields, boolean>>({
  name: false,
  surname: false,
  company: false,
  position: false,
  email: false,
  phoneNumber: false,
  privacyConsent: false,
});

function validate(values = fields) {
  const e: Record<string, string> = {};

  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRe = /^[+()\-.\s\d]{7,20}$/;

  if (!values.name.trim()) e.name = "Por favor, introduce tu nombre.";
  else if (values.name.trim().length < 2)
    e.name = "El nombre debe tener al menos 2 caracteres.";

  if (!values.surname.trim()) e.surname = "Por favor, introduce tu apellido.";
  else if (values.surname.trim().length < 2)
    e.surname = "El apellido debe tener al menos 2 caracteres.";

  if (values.company && values.company.trim().length < 2)
    e.company = "La empresa debe tener al menos 2 caracteres.";

  if (values.position && values.position.trim().length < 2)
    e.position = "El cargo debe tener al menos 2 caracteres.";

  if (!values.email.trim())
    e.email = "Por favor, introduce tu correo electrónico.";
  else if (!emailRe.test(values.email))
    e.email = "Introduce un correo electrónico válido.";

  if (!values.phoneNumber.trim())
    e.phoneNumber = "Por favor, introduce tu número de teléfono.";
  else if (!phoneRe.test(values.phoneNumber))
    e.phoneNumber = "Introduce un número de teléfono válido.";

  if (!values.privacyConsent)
    e.privacyConsent =
      "Debes aceptar la política de privacidad para continuar.";

  return e;
}

const errors = computed(() => validate());

function markTouched<K extends keyof typeof touched>(key: K) {
  touched[key] = true;
}

const onSubmit = async () => {
  (Object.keys(touched) as Array<keyof typeof touched>).forEach(
    (k) => (touched[k] = true)
  );

  const current = validate();
  if (Object.keys(current).length > 0) {
    toast.error({
      title: "Faltan datos",
      message: "Por favor, corrige los campos marcados en rojo.",
    });

    const map: Record<string, string> = {
      name: "nombre",
      surname: "apellido",
      phoneNumber: "telefono",
      position: "cargo",
      company: "empresa",
      email: "email",
    };
    const firstKey = Object.keys(current)[0];
    const nameAttr = map[firstKey] ?? firstKey;
    document.querySelector<HTMLElement>(`[name="${nameAttr}"]`)?.focus();
    return;
  }

  try {
    const payload = JSON.parse(JSON.stringify(fields));
    const data = await sendRequest(payload);

    // Éxito
    toast.success({
      title: "¡Solicitud enviada!",
      message:
        "Hemos recibido tu solicitud. Nos pondremos en contacto contigo en breve.",
    });

    fields.name = "";
    fields.surname = "";
    fields.company = "";
    fields.position = "";
    fields.email = "";
    fields.phoneNumber = "";
    fields.privacyConsent = false;

    touched.name = false;
    touched.surname = false;
    touched.company = false;
    touched.position = false;
    touched.email = false;
    touched.phoneNumber = false;
    touched.privacyConsent = false;
  } catch (err: any) {
    const res = err?.response?.data ?? err?.data ?? err;

    // API Platform 422 con "violations"
    if (Array.isArray(res?.violations) && res.violations.length) {
      // Marca como tocados y deja que tu validate() pinte los errores
      res.violations.forEach((v: any) => {
        const key = v.propertyPath as keyof typeof touched;
        if (key in touched) touched[key] = true;
      });

      toast.error({
        title: "No se pudo enviar",
        message: "Revisa los campos con errores e inténtalo de nuevo.",
      });
      return;
    }

    // Error genérico / red
    toast.error({
      title: "Error al enviar",
      message:
        "Ha ocurrido un error inesperado. Inténtalo de nuevo en unos minutos.",
    });
    console.error(err);
  }
};
</script>
<style scoped>
.main {
  border-radius: 30px;
  background: linear-gradient(180deg, #476eb7 0%, #123373 100%);
}
::v-deep(.custom-label-check-box) {
  color: white;
}
button {
  border-radius: 40px;
  background: #07399d;
  box-shadow: 0 1px 9.1px 4px rgba(26, 122, 255, 0.3);
  display: flex;
  width: 100%;
  height: 60px;
  padding: 18px 60px;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;
  color: #f3f4f8;
  text-align: center;
  font-family: "Montserrat";
  font-size: 20px;
  font-style: normal;
  font-weight: 900;
  letter-spacing: 0.2px;
  text-transform: capitalize;
  transition: all 0.3s ease;
}
button:hover {
  background: #052f84;
  box-shadow: 0 1px 9.1px 4px rgba(26, 122, 255, 0.3);
}
</style>
